import { useEffect, useId, useRef, useState } from "react";

// Debounced type-ahead used for both the sales-rep picker (header) and the
// write-off "View As User" picker. `fetcher(search)` resolves to
// [{id, name, ...}]; `renderItem` can add secondary text (title/branch).
// Selecting an item puts its name in the input and calls onSelect(item).
const Autocomplete = ({
  placeholder,
  fetcher,
  onSelect,
  renderItem,
  inputClassName = "",
}) => {
  const [value, setValue] = useState("");
  const [items, setItems] = useState(null); // null = dropdown closed
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);
  const listId = useId();
  const timerRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setItems(null);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => {
      document.removeEventListener("click", onDocClick);
      clearTimeout(timerRef.current);
    };
  }, []);

  const handleChange = (e) => {
    const next = e.target.value;
    setValue(next);
    clearTimeout(timerRef.current);
    const term = next.trim();
    if (term.length < 2) {
      setItems(null);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const results = await fetcher(term);
        setItems(Array.isArray(results) ? results : []);
        setHighlight(-1);
      } catch {
        // Search failures just leave the dropdown closed; the Run/Load
        // button surfaces real connectivity errors.
        setItems(null);
      }
    }, 250);
  };

  const choose = (item) => {
    setValue(item.name);
    setItems(null);
    onSelect(item);
  };

  const handleKeyDown = (e) => {
    if (!items || !items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      choose(items[highlight]);
    } else if (e.key === "Escape") {
      setItems(null);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-controls={listId}
        aria-label={placeholder}
        aria-autocomplete="list"
        aria-expanded={items !== null}
        className={`h-10 w-full min-w-[220px] rounded-xl border px-3 text-sm ${inputClassName}`}
      />
      {items !== null && (
        <div
          id={listId}
          role="listbox"
          className="app-glass absolute left-0 right-0 top-full z-50 mt-1.5 max-h-60 min-w-[240px] overflow-y-auto rounded-xl border p-1"
        >
          {items.length === 0 ? (
            <div className="app-muted px-3 py-2 text-sm italic">
              No matches found
            </div>
          ) : (
            items.map((item, i) => (
              <div
                key={`${item.id ?? ""}-${i}`}
                role="option"
                aria-selected={i === highlight}
                onClick={() => choose(item)}
                onMouseEnter={() => setHighlight(i)}
                className={`cursor-pointer rounded-lg px-3 py-2 text-sm transition-colors ${
                  i === highlight ? "bg-amber-500/15" : ""
                }`}
              >
                {renderItem ? renderItem(item) : item.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
